<template>
  <div class="BuyBoxSamplesOnly">
    <!-- * * * * order samples
    Don't show samples if we are already on a sample page
    -->
    <div v-if="marketSample?.partNo && !activeVariant.partNo?.startsWith('SA0')" class="mt-16">
      <button
        class="btn w-full"
        :class="[props.buttonClass ?? 'btn--lg', { loading }]"
        type="button"
        @click="addSampleToCart()"
      >
        <img
          :src="props.buttonClass?.includes('btn--secondary') ? '/icons/cart.svg' : '/icons/cart-inv.svg'"
          class="inline-block w-20 mr-16"
          alt=""
        >
        <span v-if="cartStore.getQuantityFromPartNo(marketSample?.partNo) > 0">
          {{ $t('quickbuy.button.inCart') }}
        </span>
        <span v-else-if="newDesign">
          {{ $t('pdp.experience.samplePanel.button', { price: useLocalizedCurrency(samplePrice, true) }) }}
        </span>
        <span v-else>
          {{ $t('samples.b2c.free') }} - {{ useLocalizedCurrency(samplePrice, true) }}
        </span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ProductVariantModel } from '#models/productVariant';
import { NorceRelatedProductTypeCodes } from '#constants/norceCodes';
import useAlgoliaFetch from '~/composables/useAlgoliaFetch';
import type { ProductModel } from '#models/product';
import Product from '#models/product';
import { useLocalizedCurrency } from '~/composables/useLocalizedCurrency';
import { useCartStore } from '~/store/cart';
import useLinkReplacer from '~/composables/useLinkReplacer';
import { useGlobalContentStore } from '~/store/globalContent';

const { getExactVariantByPartNo, lastError, lastErrorCode } = useAlgoliaFetch();
const cartStore = useCartStore();
const globalContent = useGlobalContentStore();

const props = defineProps<{
  productItem: ProductModel,
  activeVariant: ProductVariantModel,
  buttonClass?: string,
  newDesign?: boolean,
}>();

const emit = defineEmits<{
  (e: 'loaded', available: boolean, price: number): void;
}>();

onMounted(()=> {
  setTimeout(loadSamples, 100);
});

watch(() => props.activeVariant, () => {
  loadSamples();
});

/**
 * Unfortunatly we have to load the samples to see if we even should show the button
 * since it's possible it's not on the users pricelist
 *
 * B2C should only have one sample, so we slice the array
 */
const samplesHydrated = ref<ProductModel[]>([]);
const marketSample = ref<ProductModel>();

const loadSamples = async() => {
  marketSample.value = undefined;
  samplesHydrated.value = [];

  const samples = props.activeVariant.relatedProducts.filter(f => f.code === NorceRelatedProductTypeCodes.Samples).map(m => m.partNo);
  if (samples.length) {
    const res = await getExactVariantByPartNo(samples);
    if (res) {
      samplesHydrated.value = res.hits.map(hit => Product.create([hit]));
      if (globalContent.getIsB2c) {
        if (globalContent.domainMap?.countryCode === 'us') {
          marketSample.value = samplesHydrated.value?.find((s) => s.partNo?.toUpperCase().startsWith('US01'));
        } else {
          marketSample.value = samplesHydrated.value?.find((s) => s.partNo?.toUpperCase().startsWith('SA01'));
        }
      } else {
        marketSample.value = samplesHydrated.value[0];
      }
    } else {
      console.log(lastError.value);
      console.log(lastErrorCode.value);
    }
  }
  emit('loaded', Boolean(marketSample.value?.partNo), samplePrice.value);
};

const loading = ref(false);

const samplePrice = computed(() => {
  const priceInfo = marketSample.value?.variants[0]?.priceInfo;
  const priceStr = globalContent.getDisplayIncVat
    ? priceInfo?.price
    : priceInfo?.priceBeforeVat;
  return parseFloat((priceStr ?? '0').replaceAll(',', '')) || 0;
});

const { manualUrl } = useLinkReplacer();

const addSampleToCart = async() => {
  if (!marketSample.value) { return; }
  if (cartStore.getQuantityFromPartNo(marketSample.value.partNo) > 0) {
    navigateTo(manualUrl('/checkout'));
  } else {
    loading.value = true;
    await cartStore.updateCart(marketSample.value.partNo, 1, -1, true, marketSample.value);
    loading.value = false;
  }
};

const inCart = computed(() => cartStore.getQuantityFromPartNo(marketSample.value?.partNo) > 0);

defineExpose({ addSampleToCart, loading, inCart });

</script>
